import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface PageDemandSideSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  detail?: string;
}

export interface PageDemandRealizationSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  position: number | null;
  searchVolume: number | null;
  detail?: string;
}

export interface PageDemandSnapshot {
  claim: PageDemandSideSnapshot | null;
  claimIntentTokens?: string[];
  realization: PageDemandRealizationSnapshot | null;
  ambientTopic: PageDemandSideSnapshot | null;
  splitReasons: string[];
  overrideClaim?: PageDemandOverrideSnapshot | null;
  computedAt: string | null;
}

export interface PageDemandOverrideSnapshot {
  label: string;
  reason: 'wrong_intent' | 'strategist' | 'gsc';
  createdAt: string;
  createdBy: string;
}

export interface PageRealizationCeilingDriverSnapshot {
  keyword: string;
  searchVolume: number;
  matchingTokens: string[];
  upsideAtTargetRank: number;
}

export interface PageRealizationSnapshot {
  realizedVisibility: number;
  realizedSource: 'etv' | 'traffic_proxy';
  demandCeiling: number | null;
  ceiling: number;
  realizationRatio: number | null;
  gap: number | null;
  gapGate: 'scored' | 'low_confidence' | 'pending' | 'unscored';
  ceilingDriver: PageRealizationCeilingDriverSnapshot | null;
  alignedKeywordCount: number;
}

export interface PageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored';
  reasons: string[];
  model: 'realization_gap' | 'eligibility' | 'unscored';
}

export interface SecondaryPageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored' | null;
  model: 'none' | 'freshness_hold' | 'earned_visibility';
  reasons: string[];
  recentlyUpdated: boolean;
  unscoredSegment:
    | 'no_claim'
    | 'low_confidence_claim'
    | 'no_market_data'
    | 'no_aligned_ceiling'
    | 'immaterial_ceiling'
    | null;
}

export type PageSerpQuerySource =
  | 'strategist_override'
  | 'realization'
  | 'ceiling_driver'
  | 'claim_fallback';
export type PageSerpMatchType = 'exact_url' | 'domain' | 'none';

export interface PageSerpResultRow {
  position: number;
  domain: string;
  url: string;
  title: string;
  description?: string;
  highlighted?: string[];
  publishedAt?: string | null;
}

export type PageIntentDiagnosisBulletKind =
  | 'serp_family'
  | 'placement'
  | 'lexical_gap'
  | 'lexical_misuse'
  | 'claim_mismatch'
  | 'mixed_serp'
  | 'freshness'
  | 'vocabulary_fragmented'
  | 'answer_structure'
  | 'no_serp';

/** How winners deliver the answer in snippets (FW-URL-14). */
export type SerpAnswerStructureFamily =
  | 'item_list'
  | 'comparative_contrast'
  | 'numbered_howto'
  | 'tool_lander'
  | 'definitional_lede'
  | 'feature_checklist'
  | 'vs_framing'
  | 'fragmented'
  | 'unknown';

export interface SerpCollocationHead {
  head: string;
  neighbors: string[];
}

export interface PageIntentDiagnosisBullet {
  text: string;
  kind: PageIntentDiagnosisBulletKind;
  severity: 'info' | 'warn' | 'success';
}

export interface PageIntentSectionPreview {
  label: string;
  text: string;
}

export interface SerpWinnerExcerpt {
  position: number;
  domain: string;
  excerpt: string;
  highlightTerms: string[];
  /** Verbatim structure-evidence spans (FW-URL-14), substring-validated. */
  structureEvidenceSpans?: string[];
}

export interface AnswerStructureEvidenceExemplar {
  position: number;
  spans: string[];
  why?: string;
}

export interface SerpMixedIntentSample {
  position: number;
  domain: string;
  family: string;
}

export interface SerpRowIntent {
  position: number;
  family: string;
}

export interface PageIntentDiagnosis {
  serpFamily: string;
  serpConsensusRatio: number;
  serpMixedIntentSamples: SerpMixedIntentSample[];
  serpRowIntents: SerpRowIntent[];
  bestSegmentLabel: string;
  bestSegmentFamily: string;
  /** Page-type “Yours” chip (Title/H1/Intro preferred). Falls back to bestSegmentFamily. */
  pageTypeFamily?: string;
  pageTypeLabel?: string | null;
  intentAlignedWithSerp: boolean;
  serpLexicalTerms: string[];
  serpVocabularyShared: boolean;
  queryContextTokens: string[];
  presentLexicalTerms: string[];
  misalignedLexicalTerms: string[];
  /** Contract terms the page uses NOWHERE. Page-wide, not best-section. */
  missingTerms: string[];
  /**
   * Every contract term with where it sits on the page.
   *
   * Computed server-side across all crawled sections, so the surfaces cannot
   * disagree about whether a word is used. Optional for payloads written before
   * it existed; treat a missing array as "not measured", never as "absent".
   */
  serpTermPlacement?: Array<{ term: string; where: 'in_answer' | 'on_page' | 'absent' }>;
  serpCollocationHeads: SerpCollocationHead[];
  /** Dominant answer-delivery structure across top-N snippets (FW-URL-14). */
  answerStructureFamily?: SerpAnswerStructureFamily;
  answerStructureRatio?: number;
  answerStructureAligned?: boolean;
  answerStructurePageFamily?: SerpAnswerStructureFamily | null;
  answerStructurePageLabel?: string | null;
  /** Full section body for page structure match / closest. */
  answerStructurePageText?: string | null;
  /** Structure highlights inside the page passage. */
  answerStructurePageEvidenceSpans?: string[];
  /** Up to 3 matching (or 1 closest) structure passages. */
  answerStructurePagePassages?: Array<{
    label: string;
    text: string;
    matchKind: 'match' | 'closest';
    family?: string;
    evidenceSpans?: string[];
    questionSpans?: string[];
    headingIsQuestion?: boolean;
    /** 0–1 how strongly this passage fits the SERP structure family. */
    fitRatio?: number;
  }>;
  answerStructureSource?: 'heuristic' | 'llm';
  answerStructureCue?: string | null;
  answerStructureEvidence?: AnswerStructureEvidenceExemplar[];
  bullets: PageIntentDiagnosisBullet[];
  yourBestSection: PageIntentSectionPreview | null;
  yourIntroSection: PageIntentSectionPreview | null;
  serpWinnerExcerpts: SerpWinnerExcerpt[];
}

export interface PageSerpSnapshot {
  query: string;
  queryDisplay: string;
  querySource: PageSerpQuerySource;
  locationCode: number;
  languageName: string;
  results: PageSerpResultRow[];
  yourPosition: number | null;
  yourUrl: string | null;
  matchType: PageSerpMatchType;
  fetchedAt: string;
  dataSource: 'database' | 'api';
  /** Labs keyword difficulty 0–100 for the target query (SERP competitiveness). */
  keywordDifficulty: number | null;
  keywordDifficultyDataSource: 'database' | 'api' | null;
}

/**
 * The compare target and its facts (COMPARE_FIRST_URL_REVIEW.md §3).
 * Mirrors `backend/src/utils/compare-summary.ts` — see there for what each
 * state means and why they are not collapsed.
 */
export type CompareSummaryState =
  | 'ready'
  | 'target_uncrawled'
  | 'rival_crawl_failed'
  | 'target_not_retrieved'
  | 'you_are_top'
  | 'no_target'
  | 'no_serp';

export type CompareFactKey =
  | 'contextual_outlinks'
  | 'word_count'
  | 'estimated_traffic'
  | 'ranked_keywords'
  | 'onpage_score';

export interface CompareSummaryFact {
  key: CompareFactKey;
  yours: number | null;
  theirs: number | null;
  winner: 'you' | 'them' | 'tie' | 'unknown';
}

export interface CompareSummaryTarget {
  url: string;
  domain: string;
  position: number | null;
  kind: 'serp_winner' | 'topical_peer';
  reason: string;
  isMarkedCompetitor: boolean;
}

export interface CompareSummary {
  state: CompareSummaryState;
  target: CompareSummaryTarget | null;
  query: string | null;
  skippedNote: string | null;
  facts: CompareSummaryFact[];
  missingSectionCount: number | null;
  incomingLinksAvailable: false;
}

export interface UrlAnalysisResponse {
  success: boolean;
  data: {
    url: string;
    urlId?: string;
    planClusters?: string[];
    analysis: any | null;
    pageDemand?: PageDemandSnapshot | null;
    pageRealization?: PageRealizationSnapshot | null;
    pageHealth?: PageHealthSnapshot | null;
    pageSecondaryHealth?: SecondaryPageHealthSnapshot | null;
    pageSerp?: PageSerpSnapshot | null;
    pageIntentDiagnosis?: PageIntentDiagnosis | null;
    pageCompare?: CompareSummary | null;
    message?: string;
    status: 'completed' | 'pending';
  };
  timestamp: string;
}

export interface RefetchPageAnalysisResponse {
  success: boolean;
  data: {
    url: string;
    status: 'completed' | 'pending';
    taskId: string | null;
    metadata?: {
      dataSource: 'database' | 'api';
      lastUpdated?: string;
      taskId?: string;
    };
  };
  timestamp: string;
}

export interface UrlAnalysisStatusResponse {
  success: boolean;
  data: {
    status: 'completed' | 'pending' | 'failed';
    analysis?: any;
    error?: string;
    metadata?: {
      dataSource?: 'database' | 'api';
      lastUpdated?: string;
    };
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataForSEOService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get the latest PageAnalysis for a specific URL from the database.
   * Calls GET /api/v1/urls/analysis?url=<encoded>.
   *
   * Note: Analyses are created server-side via the page comparison flow
   * (POST /api/v1/urls/compare → DataForSEO pingback → PageAnalysis).
   */
  getUrlAnalysis(
    url: string,
    options?: { refreshAnswerStructure?: boolean }
  ): Observable<UrlAnalysisResponse> {
    const qs = new URLSearchParams();
    qs.set('url', url);
    if (options?.refreshAnswerStructure) {
      qs.set('refreshAnswerStructure', '1');
    }
    return this.http.get<UrlAnalysisResponse>(`${this.API_URL}/urls/analysis?${qs.toString()}`);
  }

  /** Dev-only: force a fresh single-page analysis for this URL. */
  refetchSinglePageAnalysis(url: string): Observable<RefetchPageAnalysisResponse> {
    return this.http.post<RefetchPageAnalysisResponse>(
      `${this.API_URL}/urls/process/refetch-page-analysis`,
      { url }
    );
  }

  /** Poll backend task status for a page-analysis task ID. */
  getUrlAnalysisStatus(taskId: string): Observable<UrlAnalysisStatusResponse> {
    return this.http.get<UrlAnalysisStatusResponse>(`${this.API_URL}/urls/analysis-status/${taskId}`);
  }

  /** Title + H1 from stored PageAnalysis for supporting-page token similarity. */
  getSupportingPageLexical(urls: string[]): Observable<{
    success: boolean;
    data: { byUrl: Record<string, { title: string; h1: string }> };
  }> {
    return this.http.post<{
      success: boolean;
      data: { byUrl: Record<string, { title: string; h1: string }> };
    }>(`${this.API_URL}/urls/supporting-page-lexical`, { urls });
  }

  /** Supporting-page opportunity eval: embedding page-match + content-gap scoring. */
  postSupportingPagesOpportunityEval(body: {
    focalUrl: string;
    competitorUrls: string[];
    yourCandidateUrls: string[];
    focalQuery?: string;
    v1ByCompetitor: Array<{
      competitorUrl: string;
      similarUrl: string | null;
      score: number | null;
      v1CompetitorPreview?: string;
      v1YourMatchPreview?: string | null;
    }>;
  }): Observable<{ success: boolean; data: SupportingPagesV2EvalData }> {
    // Memoised for the session, and the memo is what makes review mode's
    // prefetch work at all.
    //
    // This POST is the slow half of opening a review card — ~4.9s on the pilot,
    // because the SERP-ranked rival is not in the precomputed set (see
    // TECHNICAL_DEBT 0ae). Angular destroys and rebuilds the embed on every
    // step of the belt, so without a memo the same evaluation is re-issued from
    // scratch each time a page is opened, including a page that was just
    // prefetched or one the operator steps back to.
    //
    // `shareReplay` rather than a plain result cache so an in-flight request is
    // shared too: when the operator advances onto a page the prefetch is still
    // evaluating, they join that request instead of starting a second one
    // against a sidecar that is effectively single-threaded (0ad).
    //
    // Safe to memoise for a session: the inputs are stored page analyses, which
    // a crawl changes and a review pass does not. The key includes the
    // competitor set so a different question is a different entry.
    const key = [
      body.focalUrl.trim(),
      [...body.competitorUrls].sort().join(','),
      [...body.yourCandidateUrls].sort().join(',')
    ].join('|');

    const hit = this.opportunityEvalCache.get(key);
    if (hit) return hit;

    const request = this.http
      .post<{ success: boolean; data: SupportingPagesV2EvalData }>(
        `${this.API_URL}/urls/supporting-pages-opportunity-eval`,
        body
      )
      .pipe(
        // A failure must not be remembered as an answer.
        catchError((err) => {
          this.opportunityEvalCache.delete(key);
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    // Bounded: the belt is at most a few hundred pages and each entry holds one
    // payload, but a long-lived tab should not grow without limit.
    if (this.opportunityEvalCache.size >= 300) this.opportunityEvalCache.clear();
    this.opportunityEvalCache.set(key, request);
    return request;
  }

  /** Session memo for the evaluation above — see the note there. */
  private readonly opportunityEvalCache = new Map<
    string,
    Observable<{ success: boolean; data: SupportingPagesV2EvalData }>
  >();

  getSupportingPagesByTopicSnapshot(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SupportingPagesByTopicSnapshotData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SupportingPagesByTopicSnapshotData | null;
    }>(`${this.API_URL}/urls/supporting-pages-by-topic-snapshot`, {
      params: { url }
    });
  }

  getSupportingPagesOpportunitySnapshot(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SupportingPagesV2EvalData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SupportingPagesV2EvalData | null;
    }>(`${this.API_URL}/urls/supporting-pages-opportunity-snapshot`, {
      params: { url }
    });
  }

  /** Precomputed "Top SERP rival" section coverage for a focal URL (see Setup step). */
  getSerpRivalCoverage(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SerpRivalCoverageData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SerpRivalCoverageData | null;
    }>(`${this.API_URL}/urls/serp-rival-coverage`, {
      params: { url }
    });
  }

  overridePageDemandClaim(input: {
    urlId: string;
    scopeDomain: string;
    overrideClaimLabel: string;
    reason?: 'wrong_intent' | 'strategist' | 'gsc';
    skipSerpCascade?: boolean;
  }): Observable<{
    success: boolean;
    message?: string;
    data?: {
      pageDemand: PageDemandSnapshot;
      pageSerp?: PageSerpSnapshot | null;
      pageIntentDiagnosis?: PageIntentDiagnosis | null;
      serpRefresh?: {
        success: boolean;
        error?: string;
        queryDisplay?: string | null;
        snapshotsWritten?: number;
      };
      serpRivalCoverage?: SerpRivalCoverageData | null;
      serpRivalRefresh?: { success: boolean; error?: string };
    };
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data?: {
        pageDemand: PageDemandSnapshot;
        pageSerp?: PageSerpSnapshot | null;
        pageIntentDiagnosis?: PageIntentDiagnosis | null;
        serpRefresh?: {
          success: boolean;
          error?: string;
          queryDisplay?: string | null;
          snapshotsWritten?: number;
        };
        serpRivalCoverage?: SerpRivalCoverageData | null;
        serpRivalRefresh?: { success: boolean; error?: string };
      };
    }>(`${this.API_URL}/urls/${encodeURIComponent(input.urlId)}/page-demand-override`, {
      scopeDomain: input.scopeDomain,
      overrideClaimLabel: input.overrideClaimLabel,
      reason: input.reason || 'wrong_intent',
      skipSerpCascade: input.skipSerpCascade === true
    });
  }

  refreshSerpRivalCoverage(input: {
    url: string;
    scopeDomain?: string;
  }): Observable<{
    success: boolean;
    data?: {
      serpRivalCoverage: SerpRivalCoverageData | null;
      serpRivalRefresh: { success: boolean; error?: string };
    };
  }> {
    return this.http.post<{
      success: boolean;
      data?: {
        serpRivalCoverage: SerpRivalCoverageData | null;
        serpRivalRefresh: { success: boolean; error?: string };
      };
    }>(`${this.API_URL}/urls/serp-rival-coverage/refresh`, {
      url: input.url,
      scopeDomain: input.scopeDomain
    });
  }

  rebuildSupportingPagesByTopicSnapshot(focalUrl: string): Observable<{
    success: boolean;
    message?: string;
    data: SupportingPagesByTopicSnapshotData | null;
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data: SupportingPagesByTopicSnapshotData | null;
    }>(`${this.API_URL}/urls/supporting-pages-by-topic/rebuild`, { focalUrl });
  }

  rebuildSupportingPagesOpportunitySnapshot(focalUrl: string, options?: { skipV1?: boolean }): Observable<{
    success: boolean;
    message?: string;
    data: {
      supportingPages: SupportingPagesByTopicSnapshotData | null;
      opportunities: SupportingPagesV2EvalData | null;
    };
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data: {
        supportingPages: SupportingPagesByTopicSnapshotData | null;
        opportunities: SupportingPagesV2EvalData | null;
      };
    }>(`${this.API_URL}/urls/supporting-pages-opportunities/rebuild`, {
      focalUrl,
      skipV1: options?.skipV1 === true
    });
  }

}

/**
 * One competitor section measured against your whole focal page.
 * `sectionCovered` = topic addressed somewhere on your page;
 * `sectionSimilarity` = similarity to `closestMy*` (0–1);
 * `closestMy*` = nearest focal section when verifying a gap;
 * `absentTerms` = salient phrases found nowhere on your page.
 */
export interface HeadingPathSegment {
  level: number;
  heading: string;
}

export interface SectionCoverageSiblingLeaf {
  sectionNumber?: number;
  level: number;
  heading: string;
  sectionCovered: boolean;
  isGapLeaf: boolean;
}

export interface SectionCoverageGapMemberPath {
  sectionNumber?: number;
  headingPathSegments?: HeadingPathSegment[];
}

export interface ListInventoryItem {
  label: string;
  normalizedLabel: string;
  source: 'numbered_heading' | 'bullet_list';
  headingLevel?: number | null;
  sectionNumber?: number;
}

export interface ListInventoryDiff {
  detected: boolean;
  focalItemCount: number;
  competitorItemCount: number;
  competitorOnly: ListInventoryItem[];
  focalOnly: ListInventoryItem[];
  onBoth: ListInventoryItem[];
  focalItems: ListInventoryItem[];
  competitorItems: ListInventoryItem[];
}

export interface SectionCoverageRow {
  sectionNumber?: number;
  sectionNumberEnd?: number | null;
  sectionTotal?: number;
  pagePosition?: string;
  competitorHeading: string;
  competitorHeadingPath?: string;
  competitorHeadingPathSegments?: HeadingPathSegment[];
  competitorHeadingLevel?: number;
  /** Consecutive missing sibling sections collapsed for user compare UI. */
  isGapGroup?: boolean;
  groupMemberSectionNumbers?: number[];
  gapAncestorSegments?: HeadingPathSegment[];
  gapMemberPaths?: SectionCoverageGapMemberPath[];
  gapSiblingLeaves?: SectionCoverageSiblingLeaf[];
  listicleSectionRole?: 'tool_root' | 'tool_feature' | null;
  listicleToolLabel?: string | null;
  listicleToolOnFocal?: boolean | null;
  listicleSuppressed?: boolean;
  /** True when section is structural/non-article — excluded from gap scoring. */
  isNonTopical?: boolean;
  nonTopicalReason?:
    | 'generic_heading'
    | 'author_bio'
    | 'newsletter_signup'
    | 'product_cta'
    | 'rival_brand_heading'
    | 'rival_no_outline'
    | null;
  sectionCovered: boolean;
  sectionSimilarity: number;
  closestMyHeading?: string | null;
  closestMyHeadingPath?: string | null;
  closestMyHeadingPathSegments?: HeadingPathSegment[] | null;
  closestMyHeadingLevel?: number | null;
  closestMyText?: string | null;
  absentTerms: string[];
  competitorText?: string;
}

export interface SupportingPagesByTopicSnapshotData {
  focalUrl: string;
  urlId: string;
  focalAnchorTopicSlugs: string[];
  focalAnchorTopicLabels: string[];
  domains: Array<{
    domain: string;
    isYourSite: boolean;
    rows: DomainTopicUrlRowSnapshot[];
  }>;
  competitorSetHash: string;
  generatedAt: string;
}

export interface DomainTopicUrlRowSnapshot {
  url: string;
  seoIntentScore: number;
  /** Focal IDF-match vs the analysis URL (FW-URL-05); primary rank when present. */
  focalMatchScore?: number;
  estimatedOrganicTraffic?: number;
  topics: Array<{
    topicSlug: string;
    topicLabel: string;
    role: 'main' | 'supporting';
  }>;
  similarOnYourSite?: { url: string; score: number };
}

export interface RankDrivenSectionGap {
  keyword: string;
  rivalPosition: number;
  focalPosition: number | null;
  searchVolume: number;
  positionDelta: number;
  sectionHeading: string;
  sectionHeadingPath: string;
  sectionNumber?: number;
  matchStrength: 'heading' | 'body' | 'both';
  priorityScore: number;
  evidenceLine: string;
  sectionCovered: boolean;
  closestMyHeading: string | null;
  closestMyHeadingPath: string | null;
  sectionSimilarity: number;
  absentTerms: string[];
  focalCoverageLine: string;
  rivalPassage: string | null;
  rivalPassageHeading: string | null;
  rivalPassageHeadingLevel?: number | null;
  rivalPassageKind?: 'heading' | 'heading_intro' | 'sentence' | 'paragraph' | 'meta' | null;
  focalPassage: string | null;
  focalPassageHeading: string | null;
  focalPassageHeadingLevel?: number | null;
  focalPassageKind?: 'heading' | 'heading_intro' | 'sentence' | 'paragraph' | 'meta' | null;
  queryFamilyMismatch: boolean;
  mismatchNote: string | null;
}

export interface SupportingPagesV2RowResult {
  competitorUrl: string;
  v1SimilarUrl: string | null;
  v1Score: number | null;
  v2PageMatchUrl: string | null;
  v2PageMatchScore: number | null;
  v1PageGap: boolean;
  v2PageGap: boolean;
  disagree: boolean;
  missingSectionCount: number;
  rawMissingSectionCount?: number;
  listicleRelevantMissingSectionCount?: number | null;
  absentTermTotal: number;
  sectionCoverage?: SectionCoverageRow[];
  sectionCoverageGroups?: SectionCoverageRow[];
  listInventoryDiff?: ListInventoryDiff | null;
  focalBodySource: string;
  competitorBodySource: string;
  focalGapSkipped: boolean;
  /** Set when page bodies exceeded the content-gap char limit and were truncated. */
  sectionCoverageWarning?: string | null;
  error: string | null;
  comparedText: SupportingPagesV2ComparedText | null;
  novelVsFocal: string;
  novelVsFocalLabel: string;
  coveredElsewhereUrl: string | null;
  coveredElsewhereSource: string;
  coveredElsewhereLabel: string;
  suggestedAction: string;
  suggestedActionLabel: string;
  opportunityPriority: number;
  /** FW-URL-10: keyword + traffic + rival section evidence. */
  rankDrivenGaps?: RankDrivenSectionGap[];
}

export interface SupportingPagesV2ComparedText {
  pageMatch: {
    v1Competitor: string;
    v1YourMatch: string | null;
    v2CompetitorQuery: string;
    v2YourMatch: string | null;
  };
  focalGap: {
    myContent: string | null;
    competitorContent: string | null;
    skipped: boolean;
    skipReason: string | null;
  };
}

/** Precomputed "Top SERP rival" section coverage for one focal URL. */
export interface SerpRivalCoverageData {
  focalUrl: string;
  rivalUrl: string;
  rivalDomain: string;
  rivalPosition: number;
  yourPosition: number | null;
  query: string;
  /** `eval_unavailable`: rival crawled fine, section eval could not run. */
  status: 'ready' | 'rival_crawl_failed' | 'eval_unavailable' | 'skipped';
  /** Only meaningful when `status` is `ready` — 0 otherwise, and 0 is not a measurement. */
  missingSectionCount: number;
  evalRow: SupportingPagesV2RowResult | null;
  generatedAt: string;
}

export interface SupportingPagesV2EvalData {
  serviceAvailable: boolean;
  serviceUrl: string;
  serviceVersion: string | null;
  focalUrl: string;
  summary: {
    competitorCount: number;
    v1GapCount: number;
    v2PageGapCount: number;
    disagreementCount: number;
    focalMacroGapTotal: number;
    strongNoveltyCount: number;
    actionableCount: number;
    newSupportingPageCount: number;
  };
  rows: SupportingPagesV2RowResult[];
} 